import type { ReactNode } from 'react';
import type { InterviewInviteDetailDto } from '@sara/api-client';
import { Link } from '@tanstack/react-router';
import { Button } from '@sara/ui/components/button';
import { TooltipProvider } from '@sara/ui/components/tooltip';
import { useDialog } from '@sara/ui/hooks/use-dialog';
import { cn } from '@sara/ui/lib/utils';
import {
  ArrowUpRight,
  CalendarClock,
  Check,
  FileText,
  Link as LinkIcon,
  Mail,
  RotateCcw,
  ShieldCheck,
  ShieldX,
  Trash2,
} from 'lucide-react';
import { useCopyToClipboard } from '../../../../hooks/useCopyToClipboard';
import { useActiveMembership, useOrgSlug } from '../../../auth';
import { ReplayDialog } from '../../containers/ReplayDialog/ReplayDialog';
import { InterviewStatusBadge } from '../InterviewStatusBadge';
import { AttemptMeter } from '../CallAttempts';
import { ExternalIdentifiersCard } from './ExternalIdentifiersCard';
import { fullName, initials, inviteValidity } from './format';

export interface InterviewBriefDetailProps {
  invite: InterviewInviteDetailDto;
  /** Renders the call-attempts section (selector + per-attempt results & transcript). */
  renderAttempts: () => ReactNode;
  /** Opens the delete-confirmation flow. Deletion is admin-only on the API, so the button only shows for admins. */
  onDelete?: () => void;
}

export function InterviewBriefDetail({
  invite,
  renderAttempts,
  onDelete,
}: InterviewBriefDetailProps) {
  const orgSlug = useOrgSlug();
  const { isAdmin } = useActiveMembership();
  const { copied, copy } = useCopyToClipboard();
  const replayDialog = useDialog(ReplayDialog);
  const validity = inviteValidity(invite.expiresAt);
  // See InterviewStatusCell: only flag a depleted meter when tries ran out
  // without completing.
  const depleted = invite.attempts >= invite.maxAttempts && invite.status !== 'COMPLETED';

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        <header className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
            <div className="flex min-w-0 items-center gap-3.5">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-[14px] font-semibold text-primary ring-1 ring-primary/20"
                aria-hidden
              >
                {initials(invite)}
              </div>
              <div className="min-w-0">
                <Link
                  to="/orgs/$orgSlug/candidates/$candidateId"
                  params={{ orgSlug, candidateId: invite.organizationCandidateId }}
                  className="group inline-flex max-w-full items-center gap-1 text-foreground transition-colors hover:text-primary"
                >
                  <h1 className="truncate font-display text-[22px] font-medium leading-tight tracking-tight">
                    {fullName(invite)}
                  </h1>
                  <ArrowUpRight
                    className="h-4 w-4 shrink-0 text-muted-foreground/50 transition-all group-hover:text-primary"
                    strokeWidth={1.8}
                  />
                </Link>
                <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                  <Mail className="h-3 w-3 shrink-0" strokeWidth={1.6} />
                  <span className="truncate">{invite.candidate.email}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Replay (reprocess / re-send webhook) only applies once the
                  interview has completed, and both actions are admin-only on the
                  API — so hide the control from non-admins rather than show an
                  action that would only 403. */}
              {isAdmin && invite.status === 'COMPLETED' && (
                <Button
                  variant="outline"
                  shape="pill"
                  size="sm"
                  className="gap-1.5"
                  onClick={() =>
                    replayDialog.open({ inviteId: invite.id, canReprocess: invite.canReprocess })
                  }
                >
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.6} />
                  Replay
                </Button>
              )}
              {isAdmin && onDelete && (
                <Button
                  variant="outline"
                  shape="pill"
                  size="sm"
                  className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  data-testid="delete-session-button"
                  onClick={onDelete}
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                  Delete
                </Button>
              )}
              <Button
                variant="outline"
                shape="pill"
                size="sm"
                className="gap-1.5"
                onClick={() => copy(invite.url)}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-success" strokeWidth={1.6} />
                    Copied
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-3.5 w-3.5" strokeWidth={1.6} />
                    Copy link
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-border bg-secondary/30 px-5 py-3">
            <MetaItem
              icon={<FileText className="h-3.5 w-3.5" strokeWidth={1.6} />}
              label="Template"
            >
              <span className="text-[13px] font-medium text-foreground">
                {invite.interviewType.name}
              </span>
            </MetaItem>

            <MetaDivider />

            <MetaItem
              icon={
                validity.expired ? (
                  <ShieldX className="h-3.5 w-3.5" strokeWidth={1.6} />
                ) : validity.perpetual ? (
                  <CalendarClock className="h-3.5 w-3.5" strokeWidth={1.6} />
                ) : (
                  <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.6} />
                )
              }
              label="Invite"
              tone={validity.expired ? 'warning' : validity.perpetual ? 'muted' : 'success'}
            >
              <span
                className={cn(
                  'text-[13px] font-medium',
                  validity.expired ? 'text-warning' : 'text-foreground'
                )}
              >
                {validity.label}
              </span>
            </MetaItem>

            <MetaDivider />

            <div className="flex items-center gap-2.5">
              <InterviewStatusBadge status={invite.status} />
              {invite.attempts > 0 && (
                <span className="text-[11px] leading-none text-muted-foreground">
                  <AttemptMeter
                    attempts={invite.attempts}
                    maxAttempts={invite.maxAttempts}
                    depleted={depleted}
                  />
                </span>
              )}
            </div>
          </div>
        </header>

        <ExternalIdentifiersCard
          externalSource={invite.externalSource}
          externalUserId={invite.externalUserId}
        />

        <section className="space-y-4">
          {invite.sessionId ? renderAttempts() : <SessionNotStartedNote />}
        </section>
      </div>
    </TooltipProvider>
  );
}

/**
 * Placeholder shown when an invite has no session yet (e.g. PENDING, or a
 * LIVE_INTERVIEW_REQUESTED invite where the candidate opted out). The header
 * above still surfaces the candidate, status, and external identifiers.
 */
function SessionNotStartedNote() {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
      <p className="font-display text-xl font-medium text-foreground">Session not yet started.</p>
      <p className="mt-2 max-w-xs text-sm text-muted-foreground">
        Results will appear here once the candidate has begun the session.
      </p>
    </div>
  );
}

function MetaDivider() {
  return <span aria-hidden className="hidden h-7 w-px bg-border sm:block" />;
}

function MetaItem({
  icon,
  label,
  tone = 'muted',
  children,
}: {
  icon: ReactNode;
  label: string;
  tone?: 'muted' | 'success' | 'warning';
  children: ReactNode;
}) {
  const iconTone =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : 'text-muted-foreground';
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className={cn('inline-flex shrink-0', iconTone)}>{icon}</span>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="text-[9.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground/70">
          {label}
        </span>
        <span className="truncate">{children}</span>
      </div>
    </div>
  );
}
